import React, { createContext, useContext, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useEventSlug } from '../../../shared/hooks/useEventSlug';
import { Modal } from '../components/Modal';
import { useTranslate } from '../../../shared/hooks/useTranslate';
import { useLayoutConfig } from './useLayoutConfig';
import { Navigate } from 'react-router-dom';
import { useAbsolutePath } from './useAbsolutePath';

const ExpiredTokenRedirect = () => {
	const { isTokenExpired } = useLayoutConfig();
	const { prefix } = useAbsolutePath();

	if (isTokenExpired) {
		return (
			<Navigate to={`${prefix}/login?state=expired-token#start`} replace />
		);
	}
	return null;
};

const LoggedInModal = () => {
	const eventSlug = useEventSlug();
	const { __ } = useTranslate();
	const { user } = useAuth();
	const { hasJustLoggedIn } = useLayoutConfig();

	const [showModal, setShowModal] = useState(hasJustLoggedIn);

	const deleteTokenMutation = useMutation({
		mutationFn: async (userId: string) => {
			await axios.post(`/users/deleteusermagictokens/${userId}`);
		},
		onSuccess: () => {
			console.info('Magic link token deleted.');
		},
		onError: (err) => {
			console.error(err);
		},
	});

	const closeModal = () => {
		if (user) {
			deleteTokenMutation.mutate(user.id);
		}

		setShowModal(false);
	};

	return (
		<Modal
			extraClassName="modal-success"
			isOpen={showModal}
			shouldCloseOnOverlayClick={false}
			onRequestClose={closeModal}
		>
			<div className="modal-header">
				<div className="tw:flex tw:items-center tw:justify-center tw:p-6">
					<img
						src="data:image/svg+xml;utf8;base64,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"
						width="100"
						height="100"
					/>
				</div>
			</div>
			<div className="modal-body tw:p-5">
				{user && (
					<p>
						{(user.firstname && user.firstname !== '') ||
						(user.lastname && user.lastname !== '') ? (
							<strong className="text-capitalize">
								{user.firstname} {user.lastname}
							</strong>
						) : (
							<strong>{user.mail}</strong>
						)}{' '}
						{__('you are now logged in.')}
					</p>
				)}

				<p className="text-muted">
					{__('This is not me?')}{' '}
					<a href={`/${eventSlug}/logout`}>{__('Log me out')}</a>
				</p>
			</div>
			<div className="modal-footer tw:border-t-0 tw:text-center">
				<button
					type="button"
					className="btn btn-default tw:w-full tw:sm:w-auto"
					onClick={() => {
						closeModal();
					}}
				>
					{__('Ok')}
				</button>
			</div>
		</Modal>
	);
};

const AuthContext = createContext<
	| {
			isLoggedIn: true;
			isAdmin: boolean;
			isTest: boolean;
			token: string;
			user: {
				id: string;
				mail: string;
				firstname: string;
				lastname: string;
				status: number | null;
			};
	  }
	| {
			isLoggedIn: false;
			isAdmin: false;
			isTest: false;
			token: null;
			user: null;
	  }
>({
	isLoggedIn: false,
	isAdmin: false,
	isTest: false,
	token: null,
	user: null,
});

type AuthResponse = {
	id: string;
	mail: string;
	firstname: string;
	lastname: string;
	status: number | null;
	isAdmin: boolean;
	isTest: boolean;
	token: string;
} | null;

const useAuthQuery = () => {
	const eventSlug = useEventSlug();

	return useQuery({
		queryKey: ['event_auth', eventSlug],
		queryFn: async () => {
			const { data } = await axios.get(`/auth/event/${eventSlug}`);
			return data as AuthResponse;
		},
		refetchOnWindowFocus: false,
		staleTime: 5000,
	});
};

const AuthProvider = ({ children }: { children?: React.ReactNode }) => {
	const authQuery = useAuthQuery();
	if (authQuery.isPending) {
		return null;
	}

	return (
		<AuthContext.Provider
			value={
				authQuery.data
					? {
							isLoggedIn: true,
							isAdmin: authQuery.data.isAdmin,
							isTest: authQuery.data.isTest,
							token: authQuery.data.token,
							user: {
								id: authQuery.data.id,
								mail: authQuery.data.mail,
								firstname: authQuery.data.firstname,
								lastname: authQuery.data.lastname,
								status: authQuery.data.status,
							},
						}
					: {
							isLoggedIn: false,
							isAdmin: false,
							isTest: false,
							token: null,
							user: null,
						}
			}
		>
			{children}
			<LoggedInModal />
			<ExpiredTokenRedirect />
		</AuthContext.Provider>
	);
};

const useAuth = () => {
	return useContext(AuthContext);
};

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
	const { isLoggedIn } = useAuth();
	const { prefix } = useAbsolutePath();

	if (!isLoggedIn) {
		return <Navigate to={`${prefix}/login`} replace />;
	}

	return children;
};

const useProtectedAuth = () => {
	const auth = useContext(AuthContext);

	if (!auth.isLoggedIn) {
		throw new Error('Authentication error.');
	}

	return auth;
};

export { AuthProvider, useAuth, ProtectedRoute, useProtectedAuth };
